import { forwardRef, useMemo, useRef } from 'react';
import { ChevronLeft, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext.jsx';
import { useLoginDrawer } from '@/context/LoginDrawerContext.jsx';
import { AVATAR_TYPES, buildGeneratedAvatar } from '@/lib/avatar.js';
import { useTranslation } from '@/components/i18n/TranslationProvider';

const baseButtonClasses =
  'inline-flex items-center justify-center rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';

const LoginDrawerTrigger = forwardRef(function LoginDrawerTrigger(
  { variant = 'mobile', className, iconOnly = false, ...props },
  ref
) {
  const { user } = useAuth();
  const { toggle } = useLoginDrawer();
  const { t } = useTranslation();
  const genericUserLabel = t('dashboard.login_drawer.generic_user');
  const pointerGestureRef = useRef(null);
  const touchGestureRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const initials = useMemo(() => {
    if (!user) return 'GS';
    return (user.full_name || user.email || genericUserLabel)
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }, [user, genericUserLabel]);

  const avatarPresentation = useMemo(() => {
    if (!user) {
      return {
        type: AVATAR_TYPES.GENERATED,
        content: (
          <span className="text-sm font-semibold uppercase text-white">{initials}</span>
        ),
      };
    }

    const isUploaded = user.avatar_type === AVATAR_TYPES.UPLOADED && user.avatar_url;
    if (isUploaded) {
      return {
        type: AVATAR_TYPES.UPLOADED,
        content: (
          <img
            src={user.avatar_url}
            alt={user.full_name || genericUserLabel}
            className="h-full w-full rounded-full object-cover"
          />
        ),
      };
    }

    const generated = buildGeneratedAvatar({
      seed: user.avatar_seed,
      displayName: user.full_name || user.display_name,
      email: user.email,
    });

    return {
      type: AVATAR_TYPES.GENERATED,
      content: (
        <span
          className="flex h-full w-full items-center justify-center rounded-full text-sm font-semibold uppercase text-white shadow-inner shadow-black/20"
          style={generated.gradient.style}
        >
          {generated.initials}
        </span>
      ),
    };
  }, [user, initials, genericUserLabel]);

  const avatarContainerClasses = useMemo(
    () =>
      cn(
        'flex h-9 w-9 items-center justify-center rounded-full overflow-hidden',
        avatarPresentation.type === AVATAR_TYPES.UPLOADED
          ? 'bg-black/40'
          : 'bg-black/10 border border-white/20'
      ),
    [avatarPresentation.type]
  );

  const clearPointerGesture = () => {
    pointerGestureRef.current = null;
  };

  const clearTouchGesture = () => {
    touchGestureRef.current = null;
  };

  const handlePointerDown = (event) => {
    if (variant !== 'edge' || event.pointerType === 'mouse') return;

    suppressNextClickRef.current = false;
    pointerGestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
  };

  const handlePointerMove = (event) => {
    const gesture = pointerGestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const deltaX = Math.abs(event.clientX - gesture.startX);
    const deltaY = Math.abs(event.clientY - gesture.startY);
    if (deltaX > 8 || deltaY > 8) {
      gesture.moved = true;
      suppressNextClickRef.current = true;
    }
  };

  const handlePointerEnd = (event) => {
    const gesture = pointerGestureRef.current;
    if (gesture && gesture.pointerId === event.pointerId && gesture.moved) {
      suppressNextClickRef.current = true;
    }
    clearPointerGesture();
  };

  const handleTouchStart = (event) => {
    if (variant !== 'edge') return;
    const touch = event.touches?.[0];
    if (!touch) return;

    // Mobile browsers synthesize a click after touchend. We handle edge-trigger
    // activation explicitly on touchend so vertical pan gestures never reopen
    // the drawer by mistake while the user is trying to scroll.
    suppressNextClickRef.current = true;
    touchGestureRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      moved: false,
    };
  };

  const handleTouchMove = (event) => {
    const gesture = touchGestureRef.current;
    const touch = event.touches?.[0];
    if (!gesture || !touch) return;

    const deltaX = Math.abs(touch.clientX - gesture.startX);
    const deltaY = Math.abs(touch.clientY - gesture.startY);
    if (deltaX > 8 || deltaY > 8) {
      gesture.moved = true;
      suppressNextClickRef.current = true;
    }

    if (gesture.moved && typeof window !== 'undefined') {
      const incrementalDeltaY = touch.clientY - gesture.lastY;
      if (incrementalDeltaY !== 0) {
        window.scrollBy({
          top: -incrementalDeltaY,
          left: 0,
          behavior: 'auto',
        });
      }
    }

    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;
  };

  const handleTouchEnd = () => {
    const gesture = touchGestureRef.current;
    if (!gesture) {
      return;
    }

    if (!gesture.moved) {
      toggle();
    }

    clearTouchGesture();
  };

  const handleTriggerClick = (event) => {
    if (variant === 'edge' && suppressNextClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
      return;
    }
    toggle();
  };

  if (variant === 'edge') {
    return (
      <button
        ref={ref}
        type="button"
        onClick={handleTriggerClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        className={cn(
          'fixed right-0 top-1/2 z-30 flex -translate-y-1/2 items-center rounded-l-2xl bg-[var(--primary)]/80 px-3 py-4 text-white shadow-lg transition hover:bg-[var(--primary)]',
          'before:absolute before:inset-y-1.5 before:-left-1 before:w-1.5 before:rounded-l-full before:bg-[var(--primary)]/60',
          'after:absolute after:inset-y-3 after:-left-2.5 after:w-1 after:rounded-l-full after:bg-[var(--primary)]/40',
          className
        )}
        aria-label={t('dashboard.login_drawer.trigger.open_label')}
        style={{ touchAction: 'pan-y' }}
        {...props}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20">
          <ChevronLeft className="h-4 w-4" />
        </div>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleTriggerClick}
      className={cn(
        baseButtonClasses,
        'h-11 w-11 bg-[var(--primary)]/40 hover:bg-[var(--primary)]/60',
        className
      )}
      aria-label={t('dashboard.login_drawer.trigger.open_label')}
      {...props}
    >
      <span className={avatarContainerClasses}>
        {iconOnly ? <User className="h-5 w-5 text-white" /> : avatarPresentation.content}
      </span>
    </button>
  );
});

export default LoginDrawerTrigger;
